// AdminPanel.js - UPDATED with Master Backup System Integration
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { verifyAdminRole, getAdminDashboardStats } from '../utils/adminHelper';

// Import existing components - behoud huidige functionaliteit
import PackageManagement from './PackageManagement';
import WerkvormenManagement from './WerkvormenManagement';
import MasterBackup from './MasterBackup'; // NEW

// Import Firebase for health checks - behoud bestaande health check functionaliteit
import { db } from '../firebase/config';
import { 
  collection, 
  getDocs, 
  addDoc, 
  serverTimestamp,
  query,
  limit 
} from 'firebase/firestore';

// Import existing CSS - gebruik bestaande styling
import './AdminPanel.css';

const AdminPanel = () => {
  const { currentUser, loading: authLoading } = useAuth();
  
  // Admin verification state
  const [adminStatus, setAdminStatus] = useState({
    isAdmin: false,
    isLoading: true,
    error: null,
    userData: null
  });
  
  // UI state - behoud bestaande interface + NEW backup tab
  const [activeSection, setActiveSection] = useState('dashboard');
  const [dashboardStats, setDashboardStats] = useState(null);
  
  // Health check state - behoud bestaande health check functionaliteit
  const [healthChecks, setHealthChecks] = useState([]);
  const [isRunningHealthCheck, setIsRunningHealthCheck] = useState(false);
  const [lastHealthCheck, setLastHealthCheck] = useState(null);

  // Verify admin role on component mount
  useEffect(() => {
    const verifyAdmin = async () => {
      if (authLoading) {
        return; // Wait for Firebase Auth to complete
      }

      if (!currentUser) {
        setAdminStatus({
          isAdmin: false,
          isLoading: false,
          error: 'Je moet ingelogd zijn om de admin panel te gebruiken',
          userData: null
        });
        return;
      }

      try {
        const result = await verifyAdminRole(currentUser);
        
        setAdminStatus({
          isAdmin: result.isAdmin,
          isLoading: false,
          error: result.isAdmin ? null : 'Je hebt geen admin rechten voor deze applicatie',
          userData: result.userData
        });

        // Load dashboard stats if admin
        if (result.isAdmin) {
          loadDashboardStats();
        }

      } catch (error) {
        console.error('Admin verificatie error:', error);
        setAdminStatus({
          isAdmin: false,
          isLoading: false,
          error: 'Admin verificatie mislukt, probeer opnieuw',
          userData: null
        });
      }
    };

    verifyAdmin();
  }, [currentUser, authLoading]);

  // Load dashboard statistics
  const loadDashboardStats = async () => {
    try {
      const result = await getAdminDashboardStats();
      if (result.success) {
        setDashboardStats(result.data);
      } else {
        console.warn('Dashboard stats load failed:', result.error);
      }
    } catch (error) {
      console.error('Dashboard stats error:', error);
    }
  };

  // BEHOUD BESTAANDE HEALTH CHECK FUNCTIONS - exact kopie van origineel
  const runHealthChecks = async () => {
    setIsRunningHealthCheck(true);
    const checks = [];
    const startTime = new Date();

    // 1. Authentication Check
    try {
      const authStatus = currentUser ? 'PASS' : 'FAIL';
      checks.push({
        name: 'Authentication System',
        status: authStatus,
        message: currentUser ? `Logged in as: ${currentUser.email}` : 'No user authenticated',
        time: new Date() - startTime
      });
    } catch (error) {
      checks.push({
        name: 'Authentication System',
        status: 'FAIL',
        message: `Error: ${error.message}`,
        time: new Date() - startTime
      });
    }

    // 2. Database Connection Check
    try {
      const usersQuery = query(collection(db, 'users'), limit(1));
      await getDocs(usersQuery);
      checks.push({
        name: 'Database Connection',
        status: 'PASS',
        message: 'Firestore connection successful',
        time: new Date() - startTime
      });
    } catch (error) {
      checks.push({
        name: 'Database Connection',
        status: 'FAIL',
        message: `Firestore error: ${error.message}`,
        time: new Date() - startTime
      });
    }

    // 3. Teams Collection Check
    try {
      const teamsQuery = query(collection(db, 'teams'), limit(1));
      const teamsSnapshot = await getDocs(teamsQuery);
      checks.push({
        name: 'Teams Collection',
        status: 'PASS',
        message: `${teamsSnapshot.size} teams accessible`,
        time: new Date() - startTime
      });
    } catch (error) {
      checks.push({
        name: 'Teams Collection',
        status: 'FAIL',
        message: `Teams access error: ${error.message}`,
        time: new Date() - startTime
      });
    }

    // 4. Email System Check
    try {
      const testEmailJob = {
        teamId: 'health-check-test',
        invites: [{
          email: 'test@example.com',
          code: 'HEALTHTEST'
        }],
        teamInfo: {
          teamName: 'Health Check Test',
          createdBy: 'System',
          organizationId: 'health-check'
        },
        status: 'test',
        createdAt: serverTimestamp(),
        createdBy: 'health-check-system'
      };

      await addDoc(collection(db, 'email_jobs'), testEmailJob);
      checks.push({
        name: 'Email System',
        status: 'PASS',
        message: 'Email job creation successful',
        time: new Date() - startTime
      });
    } catch (error) {
      checks.push({
        name: 'Email System',
        status: 'FAIL',
        message: `Email job error: ${error.message}`,
        time: new Date() - startTime
      });
    }

    // 5. Package System Check
    try {
      const packagesQuery = query(collection(db, 'packages'), limit(1));
      await getDocs(packagesQuery);
      checks.push({
        name: 'Package System',
        status: 'PASS',
        message: 'Package collection accessible',
        time: new Date() - startTime
      });
    } catch (error) {
      checks.push({
        name: 'Package System',
        status: 'FAIL',
        message: `Package system error: ${error.message}`,
        time: new Date() - startTime
      });
    }

    // 6. Responses Collection Check
    try {
      const responsesQuery = query(collection(db, 'responses'), limit(1));
      const responsesSnapshot = await getDocs(responsesQuery);
      checks.push({
        name: 'Quiz Responses',
        status: 'PASS',
        message: `${responsesSnapshot.size} responses accessible`,
        time: new Date() - startTime
      });
    } catch (error) {
      checks.push({
        name: 'Quiz Responses',
        status: 'FAIL',
        message: `Responses error: ${error.message}`,
        time: new Date() - startTime
      });
    }

    // 7. Werkvormen Collection Check
    try {
      const werkvormenQuery = query(collection(db, 'tredes'), limit(1));
      const werkvormenSnapshot = await getDocs(werkvormenQuery);
      checks.push({
        name: 'Werkvormen System',
        status: 'PASS',
        message: `${werkvormenSnapshot.size} tredes accessible`,
        time: new Date() - startTime
      });
    } catch (error) {
      checks.push({
        name: 'Werkvormen System',
        status: 'FAIL',
        message: `Werkvormen error: ${error.message}`,
        time: new Date() - startTime
      });
    }

    setHealthChecks(checks);
    setLastHealthCheck(new Date());
    setIsRunningHealthCheck(false);
  };

  const getOverallStatus = () => {
    if (healthChecks.length === 0) return 'UNKNOWN';
    const failedChecks = healthChecks.filter(check => check.status === 'FAIL');
    if (failedChecks.length === 0) return 'HEALTHY';
    if (failedChecks.length < healthChecks.length / 2) return 'WARNING';
    return 'CRITICAL';
  };

  // BEHOUD BESTAANDE HEALTH CHECK RENDER - exact kopie
  const renderHealthCheck = () => {
    const overallStatus = getOverallStatus();
    
    return (
      <div className="admin-content-card">
        <div className="health-check-header">
          <h2>🥽 System Health Check</h2>
          <button
            onClick={runHealthChecks}
            disabled={isRunningHealthCheck}
            className={`health-check-btn ${isRunningHealthCheck ? 'health-check-btn--loading' : ''}`}
          >
            {isRunningHealthCheck ? '⏳ Checking...' : '🔄 Run Health Check'}
          </button>
        </div>

        {/* Overall Status Card */}
        {healthChecks.length > 0 && (
          <div className={`health-overall-status health-overall-status--${overallStatus.toLowerCase()}`}>
            <div className="health-overall-icon">
              {overallStatus === 'HEALTHY' && '🟢'}
              {overallStatus === 'WARNING' && '🟡'}
              {overallStatus === 'CRITICAL' && '🔴'}
              {overallStatus === 'UNKNOWN' && '⚪'}
            </div>
            <h3 className="health-overall-title">
              System Status: {overallStatus}
            </h3>
            {lastHealthCheck && (
              <p className="health-overall-timestamp">
                Last checked: {lastHealthCheck.toLocaleString()}
              </p>
            )}
          </div>
        )}

        {/* Individual Health Checks */}
        {healthChecks.length > 0 && (
          <div className="health-checks-grid">
            {healthChecks.map((check, index) => (
              <div key={index} className={`health-check-card health-check-card--${check.status.toLowerCase()}`}>
                <div className="health-check-card-header">
                  <span className="health-check-icon">
                    {check.status === 'PASS' ? '✅' : '⚠'}
                  </span>
                  <h4 className="health-check-name">
                    {check.name}
                  </h4>
                </div>
                <p className="health-check-message">
                  {check.message}
                </p>
                <div className="health-check-meta">
                  <span>Status: {check.status}</span>
                  <span>Response: {check.time}ms</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Initial State */}
        {healthChecks.length === 0 && (
          <div className="health-check-empty">
            <div className="health-check-empty-icon">🩺</div>
            <h3>No health checks run yet</h3>
            <p>Click "Run Health Check" to test all system components</p>
          </div>
        )}

        {/* Info Panel */}
        <div className="health-check-info">
          <h4>ℹ️ Health Check Information</h4>
          <ul>
            <li>Tests all critical system components</li>
            <li>Green (✅) = System working correctly</li>
            <li>Red (⚠) = System has issues</li>
            <li>Run before each deployment to verify everything works</li>
            <li>Test email jobs are marked as 'test' status and won't send actual emails</li>
          </ul>
        </div>
      </div>
    );
  };

  // UPDATED DASHBOARD met Firebase user info + Backup Status
  const renderDashboard = () => {
    return (
      <div className="admin-content-card">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '30px' 
        }}>
          <h2>Dashboard Overzicht</h2>
          
          {/* Admin User Info */}
          {adminStatus.userData && (
            <div style={{ 
              fontSize: '0.875rem', 
              color: '#6b7280',
              textAlign: 'right',
              background: '#f8fafc',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <div><strong>Admin:</strong> {adminStatus.userData.name}</div>
              <div><strong>Email:</strong> {currentUser.email}</div>
              <div><strong>Org:</strong> {adminStatus.userData.organizationId}</div>
              <div><strong>Role:</strong> {adminStatus.userData.role}</div>
            </div>
          )}
        </div>
        
        <div className="dashboard-stats-grid">
          <div className="dashboard-stat-card dashboard-stat-card--blue">
            <div className="dashboard-stat-value">{dashboardStats?.totalTeams || '12'}</div>
            <div className="dashboard-stat-label">Totaal Teams</div>
          </div>
          <div className="dashboard-stat-card dashboard-stat-card--green">
            <div className="dashboard-stat-value">{dashboardStats?.totalResponses || '89'}</div>
            <div className="dashboard-stat-label">Quiz Invullingen</div>
          </div>
          <div className="dashboard-stat-card dashboard-stat-card--orange">
            <div className="dashboard-stat-value">{dashboardStats?.emailsSent || '156'}</div>
            <div className="dashboard-stat-label">Emails Verstuurd</div>
          </div>
        </div>

        {/* UPDATED Security Status */}
        <div className="admin-status-card admin-status-card--green">
          <h3>🔒 Secure Admin Access Active</h3>
          <p>
            Authenticatie via Firebase Auth • Role-based access control • Server-side verificatie via Firestore users collectie • Geen hardcoded credentials
          </p>
        </div>

        {/* NEW: Master Backup Status */}
        <div className="admin-status-card admin-status-card--blue">
          <h3>💾 Master Backup System Actief</h3>
          <p>
            Complete database backup & restore functionaliteit beschikbaar. Maak dagelijks een snapshot van alle GezinsScan data voor maximale veiligheid. Inclusief rollback naar elk gewenst moment.
          </p>
        </div>

        <div className="admin-status-card admin-status-card--blue">
          <h3>🥽 Health Check Dashboard Actief</h3>
          <p>
            System health monitoring beschikbaar via de Health Check tab. Test alle kritieke systemen voor deployment.
          </p>
        </div>

        <div className="admin-status-card admin-status-card--blue">
          <h3>🔧 Werkvormen Management Actief</h3>
          <p>
            Je kunt werkvormen toevoegen, bewerken en verwijderen via de Werkvormen tab. Database backup en migratie tools beschikbaar.
          </p>
        </div>

        <div className="admin-status-card admin-status-card--green">
          <h3>✅ Package Management Actief</h3>
          <p>
            Je kunt packages activeren, verlengen en gebruikers beheren via de Package Management tab.
          </p>
        </div>
      </div>
    );
  };

  // Loading state
  if (authLoading || adminStatus.isLoading) {
    return (
      <div className="admin-login-container">
        <div className="admin-login-card" style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e0f2fe',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <h2 style={{ color: '#1f2937', margin: '0 0 10px 0' }}>
            Admin Toegang Verifiëren
          </h2>
          <p style={{ color: '#6b7280', margin: 0 }}>
            Controleren van admin rechten...
          </p>
        </div>
      </div>
    );
  }

  // Access denied state
  if (!adminStatus.isAdmin) {
    return (
      <div className="admin-login-container">
        <div className="admin-login-card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🚫</div>
          
          <h2 style={{
            color: '#dc2626',
            marginBottom: '15px',
            fontSize: '1.5rem'
          }}>
            Toegang Geweigerd
          </h2>
          
          <p style={{
            color: '#6b7280',
            marginBottom: '25px',
            lineHeight: '1.6'
          }}>
            {adminStatus.error}
          </p>
          
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
            <button
              onClick={() => window.history.back()}
              style={{
                background: '#6b7280',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              ← Terug
            </button>
            
            <button
              onClick={() => window.location.href = '/dashboard'}
              style={{
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main admin panel - BEHOUD BESTAANDE LAYOUT en STYLING + NEW backup tab
  return (
    <div className="admin-panel">
      
      {/* Header */}
      <div className="admin-header">
        <h1 className="admin-header-title"> GezinsScan - Admin Panel</h1>
        <button 
          onClick={() => window.location.href = '/login'} 
          className="admin-logout-btn"
        >
          Uitloggen
        </button>
      </div>

      {/* Navigation - UPDATED met nieuwe Backup tab */}
      <div className="admin-navigation">
        <div className="admin-nav-buttons">
          <button
            onClick={() => setActiveSection('dashboard')}
            className={`admin-nav-btn ${activeSection === 'dashboard' ? 'admin-nav-btn--active' : ''}`}
          >
            Dashboard
          </button>
          
          <button
            onClick={() => setActiveSection('backup')}
            className={`admin-nav-btn ${activeSection === 'backup' ? 'admin-nav-btn--active' : ''}`}
          >
            💾 Master Backup
          </button>
          
          <button
            onClick={() => setActiveSection('health')}
            className={`admin-nav-btn ${activeSection === 'health' ? 'admin-nav-btn--active' : ''}`}
          >
            🥽 Health Check
          </button>
          
          <button
            onClick={() => setActiveSection('werkvormen')}
            className={`admin-nav-btn ${activeSection === 'werkvormen' ? 'admin-nav-btn--active' : ''}`}
          >
            Werkvormen
          </button>
          
          <button
            onClick={() => setActiveSection('packages')}
            className={`admin-nav-btn ${activeSection === 'packages' ? 'admin-nav-btn--active' : ''}`}
          >
            Package Management
          </button>
        </div>
      </div>

      {/* Content - UPDATED met nieuwe Backup sectie */}
      <div className="admin-content">
        {activeSection === 'dashboard' && renderDashboard()}
        {activeSection === 'backup' && <MasterBackup />}
        {activeSection === 'health' && renderHealthCheck()}
        {activeSection === 'werkvormen' && <WerkvormenManagement />}
        {activeSection === 'packages' && <PackageManagement />}
      </div>

      {/* CSS Animation for loading spinner */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AdminPanel;